import React, { useEffect, useState } from "react";
import MasterRow from "../table/MasterRow";
import DetailRow from "../table/DetailRow";
import SbuxDialog2 from "../../../../components/SbuxDialog2";
import SbuxStackSnackbar from "../../../../components/SbuxStackSnackbar";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import PhoneNumberDialog from "../common/PhoneNumberDialog";
import DetailTable from "../table/DetailTable";
import { Link, DialogContent } from "@mui/material";
import { phoneNumberDetail } from "../../../../utils/phoneNumber";
import { useAppSelector, useAppDispatch } from "../../../../store/types";
import {
  COPIED_TO_CLIPBOARD,
  HISTORY_NOT_A_VALID_PHONE_NUMBER,
  SNACK_BARS_SEVERITY_ERROR,
  SNACK_BARS_SEVERITY_SUCCESS,
  TRANSCRIPTS_FEATURE_FLAG_NAME,
  TRANSCRIPT_PARTICIPANTS,
} from "../../../../constants";
import TranscriptContent from "../transcript";
import { getTranscriptDataByContactId } from "../../../../services/history";
import type { TranscriptResponse } from "../../../../services/history";
import { formatMillisToMinutesAndSeconds } from "../../../../utils/dateUtil";
import { omitKeyFromObject } from "../utils";
import { getCcpValidationButtonStatusSelector } from "../../../../selectors/ccpSelector";
import { getTranscriptsBucketNameSelector } from "../../../../selectors/userInstanceSelector";
import {
  getUpdatedTicketOrCaseNumberSelector,
  getTimestampSelector,
  getStatusSelector,
  getHistoryDialNumberValidateStatusSelector,
} from "../../../../selectors/historySelector";
import { selectFeatureFlags } from "../../../../selectors/featureFlagSelector";

export interface TranscriptEntry {
  BeginOffsetMillis: number;
  EndOffsetMillis: number;
  ParticipantId: string;
  Content: string;
  Sentiment?: string;
}

export interface HistoryRow {
  id?: unknown;
  timestamp?: string | null;
  transcripts?: TranscriptEntry[];
  areTranscriptsEnabledForCall?: boolean;
  ContactID?: string;
  detailsRows?: Array<{ name: string; value: string }>;
  [key: string]: unknown;
}

interface RowProps {
  row: HistoryRow;
}

const Row = ({ row }: RowProps) => {
  const ccpValidationButtonStatus = useAppSelector(
    getCcpValidationButtonStatusSelector,
  );
  const transcriptsBucketName = useAppSelector(
    getTranscriptsBucketNameSelector,
  );
  const updatedTicketOrCaseNumber = useAppSelector(
    getUpdatedTicketOrCaseNumberSelector,
  );
  const timestamp = useAppSelector(getTimestampSelector);
  const status = useAppSelector(getStatusSelector);
  const historyDialNumberValidateStatus = useAppSelector(
    getHistoryDialNumberValidateStatusSelector,
  );
  const featureFlags = useAppSelector(selectFeatureFlags);

  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  const [openTranscriptDialog, setOpenTranscriptDialog] = useState(false);
  const [openDialPrompt, setOpenDialPrompt] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showTranscriptError, setShowTranscriptError] = useState(false);
  const [parsedPhoneNumber, setParsedPhoneNumber] = useState<{
    formattedPhoneNumber: string;
    phoneNumber: string;
  } | null>(null);
  const [rowData, setRowData] = useState<HistoryRow>(row);
  const [showTranscriptCopiedSnackbar, setShowTranscriptCopiedSnackbar] =
    useState(false);

  const areTranscriptsEnabledForInstance =
    featureFlags?.find((ff) => ff?.name === TRANSCRIPTS_FEATURE_FLAG_NAME)
      ?.enabled ?? false;

  useEffect(() => {
    if (status === "updated" && rowData.timestamp === timestamp) {
      let rowUpdate = { ...rowData };
      rowUpdate["Ticket or Case #"] = updatedTicketOrCaseNumber;
      setRowData(rowUpdate);
    }
  }, [status, updatedTicketOrCaseNumber]);

  useEffect(() => {
    if (historyDialNumberValidateStatus === "failure") {
      setOpenSnackbar(true);
    }
  }, [historyDialNumberValidateStatus]);

  const handleDialNumber = async (parsedPhoneNumber: {
    formattedPhoneNumber?: string | null;
    phoneNumber?: string | null;
  }) => {
    setOpenDialPrompt(true);
    setParsedPhoneNumber({
      formattedPhoneNumber: parsedPhoneNumber.formattedPhoneNumber ?? "",
      phoneNumber: parsedPhoneNumber.phoneNumber ?? "",
    });
  };

  const handleClose = (_event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
    setShowTranscriptError(false);
    setShowTranscriptCopiedSnackbar(false);
  };

  const linkRenderer = (value: string) => {
    const parsedPhoneNumber = phoneNumberDetail(value);
    return !isNaN(Number(value)) ? (
      <Link
        component="button"
        disabled={ccpValidationButtonStatus}
        onClick={() => handleDialNumber(parsedPhoneNumber)}
      >
        {parsedPhoneNumber?.formattedPhoneNumber ?? value}
      </Link>
    ) : (
      value
    );
  };

  const toggleDialogOpen = () => {
    setOpen(!open);
  };

  const copyTranscriptInPlainTextFormat = async (
    transcripts: TranscriptEntry[] = [],
  ) => {
    let transcript = "";
    [...transcripts]
      .sort((t1, t2) => t1.BeginOffsetMillis - t2.BeginOffsetMillis)
      .map((trnscript) => {
        transcript += `${formatMillisToMinutesAndSeconds(
          trnscript?.["EndOffsetMillis"],
        )} \t ${
          (TRANSCRIPT_PARTICIPANTS as Record<string, string>)[
            trnscript?.["ParticipantId"]
          ] ?? trnscript?.["ParticipantId"]
        } \t ${trnscript?.Content} \n`;
      });
    await navigator.clipboard.writeText(transcript);
    setShowTranscriptCopiedSnackbar(true);
  };

  const transcriptBtnClickEvent = async (copyTranscript = false) => {
    let responsePayload: TranscriptResponse | undefined;
    if (
      !rowData?.transcripts &&
      !!rowData?.["ContactID"] &&
      areTranscriptsEnabledForInstance &&
      !!transcriptsBucketName &&
      !!rowData?.timestamp
    ) {
      const response = await dispatch(
        getTranscriptDataByContactId({
          contactId: rowData?.["ContactID"],
          bucketName: transcriptsBucketName,
          areTranscriptsEnabledForInstance,
          timestamp: rowData?.timestamp,
        }),
      );
      responsePayload = response?.payload as TranscriptResponse | undefined;
      if (responsePayload?.transcripts) {
        setRowData(responsePayload as HistoryRow);
      }
    }
    if (rowData?.transcripts || responsePayload?.transcripts) {
      if (copyTranscript) {
        await copyTranscriptInPlainTextFormat(
          (rowData?.transcripts || responsePayload?.transcripts) ?? [],
        );
      } else {
        setOpenTranscriptDialog(!openTranscriptDialog);
      }
    } else {
      setShowTranscriptError(!showTranscriptError);
    }
  };

  const toggleOpenDialPrompt = () => {
    setOpenDialPrompt(!openDialPrompt);
  };

  const getSnackBarMessage = () => {
    if (!!openSnackbar) {
      return "Invalid number";
    }
    if (!!showTranscriptError) {
      return "Transcripts are not available at the moment. Please try again later...";
    }
    if (!!showTranscriptCopiedSnackbar) {
      return COPIED_TO_CLIPBOARD;
    }
  };

  return (
    <>
      {rowData.detailsRows ? (
        <DetailRow row={rowData} linkRenderer={linkRenderer} />
      ) : (
        <MasterRow
          row={rowData}
          toggleDialogOpen={toggleDialogOpen}
          transcriptBtnClickEvent={transcriptBtnClickEvent}
          linkRenderer={linkRenderer}
          areTranscriptsEnabledForInstance={areTranscriptsEnabledForInstance}
          showTranscriptCopiedSnackbar={showTranscriptCopiedSnackbar}
        />
      )}
      {open && (
        <SbuxDialog2
          open={open}
          config={{
            title: <SbuxTranslate>{`Contact Details`}</SbuxTranslate>,
            disableBackdropClose: false,
          }}
          handleOnClose={toggleDialogOpen}
        >
          <DialogContent dividers>
            <DetailTable
              masterRow={omitKeyFromObject(
                "areTranscriptsEnabledForCall",
                rowData,
              )}
            />
          </DialogContent>
        </SbuxDialog2>
      )}
      {openTranscriptDialog && (
        <SbuxDialog2
          open={openTranscriptDialog}
          config={{
            title: <SbuxTranslate>{`Transcript`}</SbuxTranslate>,
            disableBackdropClose: false,
          }}
          handleOnClose={() => setOpenTranscriptDialog(!openTranscriptDialog)}
        >
          <DialogContent dividers>
            <TranscriptContent data={rowData} />
          </DialogContent>
        </SbuxDialog2>
      )}
      {openDialPrompt && (
        <PhoneNumberDialog
          openDialPrompt={openDialPrompt}
          toggleOpenDialPrompt={toggleOpenDialPrompt}
          parsedPhoneNumber={parsedPhoneNumber}
        />
      )}
      {(openSnackbar ||
        showTranscriptError ||
        showTranscriptCopiedSnackbar) && (
        <SbuxStackSnackbar
          open={
            openSnackbar || showTranscriptError || showTranscriptCopiedSnackbar
          }
          anchorOriginHorizontal={openSnackbar ? "left" : "right"}
          width={openSnackbar ? "26%" : "100%"}
          top={"18%"}
          message={
            <>
              <div style={{ fontWeight: 600 }}>
                <SbuxTranslate>{getSnackBarMessage()}</SbuxTranslate>
              </div>
              {openSnackbar && <div>{HISTORY_NOT_A_VALID_PHONE_NUMBER}</div>}
            </>
          }
          severity={
            openSnackbar || showTranscriptError
              ? SNACK_BARS_SEVERITY_ERROR
              : SNACK_BARS_SEVERITY_SUCCESS
          }
          handleClose={handleClose}
        />
      )}
    </>
  );
};

export default Row;
